"use client";

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { ref, push, serverTimestamp, set } from 'firebase/database';
import { database } from '@/lib/firebase';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    website: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ days: 28, hours: 0, minutes: 0, seconds: 0 });

  // Countdown timer effect
  useEffect(() => {
    const deadline = Date.now() + 28 * 24 * 60 * 60 * 1000; // 28 days from mount
    const interval = setInterval(() => {
      const diff = deadline - Date.now();
      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        clearInterval(interval);
        return;
      }
      const d = Math.floor(diff / (1000 * 60 * 60 * 24));
      const h = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const m = Math.floor((diff / (1000 * 60)) % 60);
      const s = Math.floor((diff / 1000) % 60);
      setTimeLeft({ days: d, hours: h, minutes: m, seconds: s });
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const { toast } = useToast();
  const router = useRouter();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validateWebsite = (website: string) => {
    try {
      new URL(website.startsWith('http') ? website : `https://${website}`);
      return true;
    } catch {
      return false;
    }
  };

  const validateForm = () => {
    if (!formData.name || !formData.email || !formData.website) {
      toast({
        title: "Required fields",
        description: "Please fill in all fields",
        variant: "destructive",
      });
      return false;
    }
    
    if (!validateEmail(formData.email)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return false;
    }
    
    if (!validateWebsite(formData.website)) {
      toast({
        title: "Invalid website",
        description: "Please enter a valid website URL",
        variant: "destructive",
      });
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar formulario
    if (!validateForm()) {
      return;
    }

    if (!validateEmail(formData.email)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    if (!validateWebsite(formData.website)) {
      toast({
        title: "Invalid website",
        description: "Please enter a valid website URL",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    
    // Exponential backoff retry configuration
    const maxRetries = 3;
    let retryCount = 0;
    let success = false;
    const baseDelay = 1000; // 1 second
    
    while (retryCount < maxRetries && !success) {
      const delay = baseDelay * Math.pow(2, retryCount); // Exponential backoff
      
      if (retryCount > 0) {
        console.log(`Retrying (${retryCount}/${maxRetries}) in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
      try {
        // Verificar conexión a internet
        if (!navigator.onLine) {
          throw new Error('No internet connection. Please check your connection.');
        }
        
        // Verificar si la base de datos está inicializada
        if (!database) {
          throw new Error('Database connection error. Please refresh the page.');
        }

        // Format website URL
        const websiteUrl = formData.website.trim().startsWith('http')
          ? formData.website.trim()
          : `https://${formData.website.trim()}`;

        // Try to save to Realtime Database
        const formRef = ref(database, 'formSubmissions');
        const newFormRef = push(formRef);
        await set(newFormRef, {
          name: formData.name.trim(),
          email: formData.email.trim(),
          website: websiteUrl,
          message: formData.message.trim(),
          status: 'new',
          createdAt: serverTimestamp(),
          timestamp: Date.now()
        });
        
        console.log('Data saved with key: ', newFormRef.key);
        
        // Success - redirect to thank you page
        success = true;
        router.push('/thank-you');
        
      } catch (error: any) {
        retryCount++;
        console.error(`Error intento ${retryCount}:`, error);
        
        // Si es el último intento o el error no es recuperable
        if (retryCount >= maxRetries || error.code === 'permission-denied') {
          let errorMessage = "There was an error submitting the form. Please try again later.";
          
          // Specific error messages
          if (error.code === 'unavailable' || !navigator.onLine) {
            errorMessage = "Could not connect to the server. Please check your internet connection.";
          } else if (error.code === 'permission-denied') {
            errorMessage = "You don't have permission to perform this action. Please contact the administrator.";
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          toast({
            title: "Error",
            description: errorMessage,
            variant: "destructive",
          });
          break;
        }
        
        // Wait before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retryCount)));
      }
    }
    
    setIsSubmitting(false);
  };

  return (
    <div className="w-full space-y-4 sm:space-y-6 md:space-y-8 mx-auto py-8">
      <h2 className="px-2 sm:px-4 md:px-12 text-primary text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-2 text-center leading-tight">Get Your Personalized Lead Report</h2>
      <p className="px-2 sm:px-4 md:px-12 text-gray-700 text-center mb-4 text-sm sm:text-base">We'll analyze your website and show you exactly why visitors aren't converting. Get your <strong>FREE detailed report</strong> in under 48 hours with specific fixes to increase your sales.</p>
      <form onSubmit={handleSubmit} className="w-full max-w-lg mx-auto">
        <div className="mb-3 sm:mb-4">
          <label htmlFor="name" className="block text-sm font-medium text-gray-600 mb-2">
            Name
          </label>
          <Input
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            placeholder="Your full name"
            className="w-full h-10 sm:h-12 text-sm sm:text-base px-3 sm:px-4 bg-gray-50 border-gray-200 focus:border-primary-500 focus:ring-primary-500"
            required
          />
        </div>
        
        <div className="mb-3 sm:mb-4">
          <label htmlFor="email" className="block text-sm font-medium text-gray-600 mb-2">
            Email
          </label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="your@email.com"
            className="w-full h-10 sm:h-12 text-sm sm:text-base px-3 sm:px-4 bg-gray-50 border-gray-200 focus:border-primary-500 focus:ring-primary-500"
            required
          />
        </div>
        
        <div className="mb-3 sm:mb-5">
          <label htmlFor="website" className="block text-sm font-medium text-gray-600 mb-2">
            Website
          </label>
          <Input
            id="website"
            name="website"
            type="url"
            value={formData.website}
            onChange={handleChange}
            placeholder="https://yourwebsite.com"
            className="w-full h-10 sm:h-12 text-sm sm:text-base px-3 sm:px-4 bg-gray-50 border-gray-200 focus:border-primary-500 focus:ring-primary-500"
            required
          />
        </div>
        
        <div className="mb-4 sm:mb-5">
          <label htmlFor="message" className="block text-sm font-medium text-gray-600 mb-2">
            Additional Message (optional)
          </label>
          <Textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Tell us more about your needs"
            className="w-full text-sm sm:text-base px-3 sm:px-4 bg-gray-50 border-gray-200 focus:border-primary-500 focus:ring-primary-500"
            rows={3}
          />
        </div>
        
        <Button 
          type="submit" 
          className="w-full bg-primary hover:bg-primary/90 text-white font-medium text-sm sm:text-base py-2.5 sm:py-3 px-4 sm:px-6 rounded transition-colors"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Getting Your Report...' : 'Get My Lead Report Now'}
        </Button>
      </form>
    </div>
  );
}
